import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  IoChevronBack,
  IoChevronForward,
  IoEye,
  IoFilter,
  IoSearch,
  IoTrash,
} from "react-icons/io5";
import { usePermission } from "hooks/usePermission";

import Card from "components/card";
import CareOfStatusBadge from "components/badges/CareOfStatusBadge";
import LeadPriorityBadge from "components/badges/LeadPriorityBadge";
import CareOfFilters from "components/filters/CareOfFilters";
import AddCareOfModal from "components/modal/AddCareOfModal";

const columnHelper = createColumnHelper();

const toTitleCase = (value) => {
  if (!value) return "-";
  const text = value.toString();
  return `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}`;
};


export default function CareOfTable(props) {
  const {
    tableData,
    isLoading,
    error,
    currentPage,
    pageSize,
    totalPages,
    onPageChange,
    onPageSizeChange,
    filters,
    onFilterChange,
    onFiltersChange,
    onClearFilters,
    isAddModalOpen,
    onCloseAddModal,
    onCreateCareOf,
    onDeleteCareOf,
    showToast,
    total,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const tableWrapperRef = useRef(null);
  const [sorting, setSorting] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = React.useMemo(() => {
    if (!filters) return 0;
    try {
      return Object.entries(filters).filter(([k, v]) => {
        if (v === undefined || v === null) return false;
        if (typeof v === "string") return v.trim() !== "";
        if (Array.isArray(v)) return v.length > 0;
        return true;
      }).length;
    } catch (e) {
      return 0;
    }
  }, [filters]);
  const { can } = usePermission();
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, careOf: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [leadsModal, setLeadsModal] = useState({ show: false, leads: [], careOf: null });

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (deleteConfirm.show || leadsModal.show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = previousOverflow;
    }
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [deleteConfirm.show, leadsModal.show]);

  const uniqueStatuses = useMemo(() => {
    const values = tableData.map((item) => toTitleCase(item.status)).filter(Boolean);
    return Array.from(new Set(values)).sort();
  }, [tableData]);

  const uniqueSource = useMemo(() => {
    const values = tableData
      .map((item) => toTitleCase(item.source || item.source))
      .filter(Boolean);
    return Array.from(new Set(values)).sort();
  }, [tableData]);

  const handleAddCareOf = async (newCareOf) => {
    if (onCreateCareOf) {
      await onCreateCareOf(newCareOf);
    }
  };

  const handleView = (careOf) => {
    const careOfId = careOf.id || careOf._id;
    navigate(`/admin/careof-management/profile/${careOfId}`, {
      state: { careOf, from: location.pathname },
    });
  };

  const handleDelete = (careOf) => {
    const leads = Array.isArray(careOf.lead) ? careOf.lead : [];

    if (leads.length === 0) {
      // No leads, go directly to delete confirmation
      setDeleteConfirm({ show: true, careOf });
    } else {
      // Show leads modal first
      setLeadsModal({ show: true, leads, careOf, loading: false });
    }
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteConfirm({ show: false, careOf: null });
  };

  const closeLeadsModal = () => {
    setLeadsModal({ show: false, leads: [], careOf: null });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.careOf) return;

    const careOfId = deleteConfirm.careOf.id || deleteConfirm.careOf._id;
    setDeleteLoading(true);

    try {
      if (onDeleteCareOf) {
        const deleted = await onDeleteCareOf(careOfId);
        if (deleted === false) {
          return;
        }
      } else if (showToast) {
        showToast("Delete handler is not available", "error");
        return;
      }
      closeDeleteModal();
    } catch (deleteError) {
      if (showToast) {
        showToast(deleteError?.response?.data?.message || "Failed to delete care of", "error");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">NAME</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">{info.getValue() || "-"}</p>
      ),
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">PHONE</p>,
      cell: (info) => (
        <p className="text-sm font-medium text-navy-700 dark:text-white">{info.getValue() || "-"}</p>
      ),
    }),
    columnHelper.accessor("company", {
      id: "company",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">COMPANY</p>,
      cell: (info) => (
        <p className="text-sm font-medium text-navy-700 dark:text-white">{info.getValue() || "-"}</p>
      ),
    }),
    columnHelper.accessor("source", {
      id: "source",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">SOURCE</p>
      ),
      cell: (info) => (
        <p className="text-sm font-medium text-navy-700 dark:text-white">
          {toTitleCase(info.getValue())}
        </p>
      ),
    }),
    columnHelper.accessor("priority", {
      id: "priority",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">PRIORITY</p>,
      cell: (info) => (
        <div className="flex items-center">
          <LeadPriorityBadge priority={info.getValue()} />
        </div>
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">STATUS</p>,
      cell: (info) => (
        <div className="flex items-center">
          <CareOfStatusBadge status={info.getValue()} />
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">ACTION</p>,
      cell: (info) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => handleView(info.row.original)}
            className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
            title="View CareOf"
          >
            <IoEye className="h-4 w-4" />
          </button>
          {can("careof_management", "delete") && (
            <button
              onClick={() => handleDelete(info.row.original)}
              className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              title="Delete CareOf"
            >
              <IoTrash className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <Card extra="h-full w-full px-6 pb-6 sm:overflow-x-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
          <div className="flex flex-wrap items-center gap-4 min-w-0">
            <div className="text-xl font-bold text-navy-700 dark:text-white whitespace-nowrap">CareOf Details</div>
            <div className="min-w-[260px] max-w-sm flex-1">
              <div className="relative">
                <IoSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={filters.search || ""}
                  onChange={(e) => onFilterChange("search", e.target.value)}
                  placeholder="Search name, phone, email, company"
                  className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-white/5 dark:text-white"
                />
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`relative flex items-center gap-2 rounded-lg px-4 py-2 transition-colors ${showFilters
              ? "bg-brand-500 text-white hover:bg-brand-600"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              }`}
          >
            <IoFilter className="h-5 w-5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 inline-flex items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                {typeof total === 'number' ? total : activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <CareOfFilters
            filters={filters}
            onFilterChange={onFilterChange}
            onFiltersChange={onFiltersChange}
            onClearFilters={onClearFilters}
            uniqueStatuses={uniqueStatuses}
            uniqueSource={uniqueSource}
            showSearch={false}
          />
        )}

        <div ref={tableWrapperRef} className={`relative mt-4 overflow-x-scroll xl:overflow-x-hidden ${table.getRowModel().rows.length === 0 ? 'min-h-[180px]' : ''}`}>
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="!border-px !border-gray-400">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className={`border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 ${header.id === "actions"
                        ? "sticky right-0 z-10 w-24 bg-white dark:bg-gray-800 text-center"
                        : "text-start cursor-pointer"
                        }`}
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">
                    Loading care of records...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={columns.length} className="py-8 text-center text-red-500">
                    {error}
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">
                    No care of records found.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => {
                  let priorityClass = "hover:bg-gray-50 dark:hover:bg-gray-800";
                  if (row.original.priority === "Urgent") {
                    priorityClass = "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30";
                  } else if (row.original.priority === "High") {
                    priorityClass = "bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30";
                  }
                  return (
                    <tr key={row.id} className={priorityClass}>
                      {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={`border-white/0 py-3 pr-4 ${cell.column.id === "actions"
                          ? "sticky right-0 z-10 w-24 bg-white dark:bg-gray-800 text-center"
                          : "min-w-[150px]"
                          }`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex items-center justify-center gap-6">
          <div className="flex items-center rounded-full bg-gray-100 p-1 dark:bg-gray-700">
            <span className="mr-2 px-2 text-sm text-gray-600 dark:text-gray-400">Rows:</span>
            {[10, 20, 50].map((size) => (
              <button
                key={size}
                onClick={() => onPageSizeChange && onPageSizeChange(size)}
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${pageSize === size
                  ? "bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-white"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  }`}
              >
                {size}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange && onPageChange(Math.max(1, currentPage - 1))}
              disabled={isLoading || currentPage <= 1}
              className="rounded-full bg-gray-100 p-2 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
            >
              <IoChevronBack className="h-4 w-4" />
            </button>
            <span className="min-w-[60px] text-center text-sm text-gray-600 dark:text-gray-400">
              {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange && onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={isLoading || currentPage >= totalPages}
              className="rounded-full bg-gray-100 p-2 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
            >
              <IoChevronForward className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Card>

      <AddCareOfModal isOpen={isAddModalOpen} onClose={onCloseAddModal} onSave={handleAddCareOf} />

      {leadsModal.show && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={closeLeadsModal}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Associated Leads
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              The following leads are associated with this CareOf and cannot be deleted.
            </p>
            <p className="mt-2 text-sm font-medium text-red-600 dark:text-red-400">
              This CareOf cannot be deleted while leads are associated with it.
            </p>

            <div className="mt-4 max-h-[400px] overflow-y-auto">
              <div className="space-y-2">
                {leadsModal.leads.map((lead) => (
                  <div
                    key={lead._id || lead.id}
                    className="flex items-center gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                  >
                    {lead.profileImage && (
                      <img
                        src={lead.profileImage}
                        alt={lead.name}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
                        {lead.name || "-"}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {lead.email || "-"}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {lead.phone || "-"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={closeLeadsModal}
                className="w-full rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm.show && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={closeDeleteModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">Delete CareOf?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This action will permanently remove
              {deleteConfirm.careOf?.name ? ` ${deleteConfirm.careOf.name}` : " this record"}.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={closeDeleteModal}
                disabled={deleteLoading}
                className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="w-full rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 sm:w-auto"
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
